import { useContext, useEffect } from 'react';
import { gql } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';

import { AppContext } from '@/App.context';
import { Issue } from '@/constants';

import config from '@/config';

export function useGetLatestIssue(): GetLatestIssueResponse {
	const { graphQLClient, parseGraphQLClientError } = useContext(AppContext);
	const cacheTime = 1000 * 60 * (config.app?.cache?.ttl?.getAllIssues || 60);
	const { data, isPending, error } = useQuery<{ issues: Array<Issue> }>({
		queryKey: ['issue', 'latest'],
		queryFn: async () =>
			graphQLClient.request(issuesQueryDocument, {
				sortKey: 'RELEASE_DATE',
				limit: 1,
				reverse: true,
				offset: 0,
			}),
		staleTime: cacheTime,
		gcTime: cacheTime,
	});

	let issue = !isPending && data?.issues.length ? data.issues[0] : null;

	return {
		issue,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

interface GetLatestIssueResponse {
	issue: Issue | null;
	isLoading: boolean;
	error?: Error | null;
}

export function useIssues(): GetIssuesResponse {
	const { graphQLClient, parseGraphQLClientError } = useContext(AppContext);
	const cacheTime = 1000 * 60 * (config.app?.cache?.ttl?.getAllIssues || 60);
	const { data, isPending, error } = useQuery<{ issues: Array<Issue> }>({
		queryKey: ['issues'],
		queryFn: async () =>
			graphQLClient.request(issuesQueryDocument, {
				sortKey: 'RELEASE_DATE',
				limit: 10,
				reverse: true,
				offset: 1,
			}),
		staleTime: cacheTime,
		gcTime: cacheTime,
	});

	return {
		issues: data?.issues || [],
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

interface GetIssuesResponse {
	issues: Array<Issue>;
	isLoading: boolean;
	error?: Error | null;
}

export function useGetFeed({ id, limit }: FeedArgs): GetFeedResponse {
	const { graphQLClient, parseGraphQLClientError } = useContext(AppContext);

	const cacheTime = 1000 * 60 * 60;
	const { data, isPending, error } = useQuery<{ issue: Issue | null }>({
		queryKey: ['issue', id, { feed: true }],
		queryFn: async () =>
			graphQLClient.request(feedQueryDocument, {
				id,
				sortKey: 'CREATED',
				reverse: true,
				limit,
			}),
		staleTime: cacheTime,
		gcTime: cacheTime,
	});

	return {
		issue: data?.issue || null,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

interface FeedArgs {
	id: string;
	limit: number;
}

interface GetFeedResponse {
	issue: Issue | null;
	isLoading: boolean;
	error?: Error | null;
}

export const feedQueryDocument = gql`
	query GetFeedIssue(
		$id: ID!
		$sortKey: ArticleSortKeys
		$reverse: Boolean
		$limit: Int
	) {
		issue(id: $id) {
			id
			name
			slug
			articles(sortKey: $sortKey, reverse: $reverse, limit: $limit) {
				id
				name
				ArticleID
				created
				updated
				releaseDate
				thumbnail
				featureImage
				slug
				description
			}
		}
	}
`;

export const issuesQueryDocument = gql`
	query GetIssues(
		$sortKey: IssueSortKeys
		$limit: Int
		$reverse: Boolean
		$offset: Int
	) {
		issues(
			sortKey: $sortKey
			limit: $limit
			reverse: $reverse
			offset: $offset
		) {
			id
			name
			description
			features
			cover
			externalUrl
			price
			currency
			slug
			data
			releaseDate
			updated
			isPaid
			created
			isPurchased
		}
	}
`;
